import React, { useRef, useState, useEffect } from "react";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import {
  Users,
  MapPin,
  Calendar,
  GraduationCap,
  Briefcase,
  Heart,
  Zap,
  Coins,
  ArrowRight,
  ChevronRight,
  ChevronLeft,
  Globe,
  Target,
  Star,
  Sparkles,
  MessageCircle,
} from "lucide-react";
import OriginalResponsesDisplay from "./OriginalResponsesDisplay";
import ScaleBar from "./ScaleBar";
import MarketsMapCard from "./MarketsMapCard";

const DemographicCard = ({
  title,
  value,
  icon: Icon,
  index,
  isExpanded,
  onToggle,
  palette,
  questionTitles = {},
}) => {
  // Different background colors for visual variety
  const bgGradients = [
    "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.03) 100%)",
    "linear-gradient(135deg, rgba(var(--primary-color-rgb), 0.12) 0%, rgba(var(--primary-color-rgb), 0.06) 100%)",
    "linear-gradient(135deg, rgba(var(--secondary-color-rgb), 0.12) 0%, rgba(var(--secondary-color-rgb), 0.06) 100%)",
    "linear-gradient(135deg, rgba(var(--accent-color-rgb), 0.12) 0%, rgba(var(--accent-color-rgb), 0.06) 100%)",
  ];

  const gradient = bgGradients[index % bgGradients.length];

  // Parse range values (e.g. "25-34" or "$50,000-$75,000")
  const parseRangeValue = (text) => {
    if (!text) return null;

    // Check if it's a range with a dash
    if (typeof text === "string" && text.includes("-")) {
      const [min, max] = text.split("-").map((s) => s.trim());
      return { min, max, isRange: true };
    }

    // Check if it's a comma-separated list
    if (typeof text === "string" && text.includes(",")) {
      return {
        values: text.split(",").map((s) => s.trim()),
        isMultiValue: true,
      };
    }

    return null;
  };

  // Format text for better readability
  const formatText = (text) => {
    if (!text) return ["No information available"];

    if (typeof text !== "string") {
      return [JSON.stringify(text)];
    }

    // If text is shorter than 200 chars, don't split it
    if (text.length < 200) return [text];

    // Split by sentences for better paragraph breaks
    const sentences = text.split(/(?<=[.!?])\s+/);
    const paragraphs = [];
    let currentParagraph = "";

    sentences.forEach((sentence) => {
      if (currentParagraph.length + sentence.length > 200) {
        paragraphs.push(currentParagraph);
        currentParagraph = sentence;
      } else {
        currentParagraph += (currentParagraph ? " " : "") + sentence;
      }
    });

    if (currentParagraph) {
      paragraphs.push(currentParagraph);
    }

    return paragraphs;
  };

  // Try to parse the value as a range or list
  const rangeData = parseRangeValue(value);

  // If not a range, format as paragraphs
  const paragraphs = !rangeData ? formatText(value) : null;

  // Helper to get left/right labels for scale
  const getScaleLabels = (title, value) => {
    if (
      typeof value === "object" &&
      value !== null &&
      (value.leftLabel || value.rightLabel)
    ) {
      return {
        leftLabel: value.leftLabel || "",
        rightLabel: value.rightLabel || "",
      };
    }
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes("formal") || lowerTitle.includes("casual")) {
      return { leftLabel: "Casual", rightLabel: "Formal" };
    }
    if (lowerTitle.includes("playful") || lowerTitle.includes("serious")) {
      return { leftLabel: "Serious", rightLabel: "Playful" };
    }
    if (lowerTitle.includes("modern") || lowerTitle.includes("traditional")) {
      return { leftLabel: "Traditional", rightLabel: "Modern" };
    }
    if (lowerTitle.includes("loud") || lowerTitle.includes("quiet")) {
      return { leftLabel: "Quiet", rightLabel: "Loud" };
    }
    if (lowerTitle.includes("bright") || lowerTitle.includes("dark")) {
      return { leftLabel: "Dark", rightLabel: "Bright" };
    }
    return { leftLabel: "Low", rightLabel: "High" };
  };

  // Visual display based on the type of data
  const renderContent = () => {
    if (!value) return "No information available";

    // Handle arrays first (for checkbox/multiselect questions like Gender, Markets, etc.)
    if (Array.isArray(value)) {
      // Special case: Markets you operate in
      if (title.trim().toLowerCase() === "markets you operate in") {
        return <MarketsMapCard regions={value} palette={palette} />;
      }
      // For other arrays (like Gender), render as chips/badges
      return (
        <div className="flex flex-wrap gap-2 mt-2">
          {value.map((item, idx) => (
            <span
              key={idx}
              className="px-2 py-1 bg-white/10 rounded-full text-xs text-white/70"
            >
              {typeof item === "object" && item !== null
                ? typeof item.value !== "undefined"
                  ? String(item.value)
                  : JSON.stringify(item)
                : String(item)}
            </span>
          ))}
        </div>
      );
    }

    // Handle string values that might be comma-separated lists
    if (typeof value === "string" && title.trim().toLowerCase() === "markets you operate in") {
      const regions = value
        .split(/,|\n|;/)
        .map((r) => r.trim())
        .filter(Boolean);
      return <MarketsMapCard regions={regions} palette={palette} />;
    }

    // Use ScaleBar for 1-10 scale values or scale objects
    const isScaleValue = (() => {
      if (
        typeof value === "object" &&
        value !== null &&
        typeof value.value === "number"
      )
        return true;
      let num = value;
      if (typeof value === "string") num = parseFloat(value);
      return typeof num === "number" && num >= 1 && num <= 10;
    })();
    if (isScaleValue) {
      let num = value;
      let leftLabel, rightLabel;
      if (
        typeof value === "object" &&
        value !== null &&
        typeof value.value === "number"
      ) {
        num = value.value;
        leftLabel = value.leftLabel;
        rightLabel = value.rightLabel;
      } else {
        if (typeof value === "string") num = parseFloat(value);
        const labels = getScaleLabels(title, value);
        leftLabel = labels.leftLabel;
        rightLabel = labels.rightLabel;
      }
      const notes = typeof value === "object" && value !== null ? value.notes : null;
      const notesVisible = typeof value === "object" && value !== null ? value.notesVisible !== false : true;
      return (
        <div>
          <ScaleBar
            value={num}
            min={1}
            max={10}
            leftLabel={leftLabel}
            rightLabel={rightLabel}
            palette={palette}
            label={title}
            customTitle={questionTitles[title]}
          />
          {notes && notesVisible && (
            <div className="mt-3 p-3 bg-white/5 rounded-lg border border-white/10">
              <p className="text-white/70 text-sm whitespace-pre-wrap">{notes}</p>
            </div>
          )}
        </div>
      );
    }

    if (rangeData && rangeData.isRange) {
      return (
        <div className="flex flex-col space-y-2">
          <div className="flex justify-between text-sm">
            <span>{rangeData.min}</span>
            <span>{rangeData.max}</span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full w-full bg-gradient-to-r from-white/20 to-white/40 rounded-full"></div>
          </div>
        </div>
      );
    }

    if (rangeData && rangeData.isMultiValue) {
      return (
        <div className="flex flex-wrap gap-2 mt-2">
          {rangeData.values.map((val, i) => (
            <span
              key={i}
              className="px-2 py-1 bg-white/10 rounded-full text-xs"
            >
              {typeof val === "object" && val !== null
                ? typeof val.value !== "undefined"
                  ? String(val.value)
                  : JSON.stringify(val)
                : String(val)}
            </span>
          ))}
        </div>
      );
    }

    // Handle objects that weren't caught by scale check
    if (typeof value === "object" && value !== null) {
      // If it has a description, use that
      if (value.description) {
        return <div className="text-white/70">{value.description}</div>;
      }
      // If it's a scale object but didn't match earlier check, try to extract value
      if (typeof value.value !== "undefined") {
        return <div className="text-white/70">{String(value.value)}</div>;
      }
      // Otherwise stringify it safely
      try {
        return (
          <div className="text-white/70 whitespace-pre-wrap">
            {JSON.stringify(value, null, 2)}
          </div>
        );
      } catch (e) {
        return <div className="text-white/70">Complex object</div>;
      }
    }

    // Default display
    return <div className="text-white/70">{String(value)}</div>;
  };

  // If this is the Markets card, render a simplified card (no expand/collapse, no click)
  if (title.trim().toLowerCase() === "markets you operate in") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: index * 0.1 }}
        className={`rounded-xl overflow-hidden backdrop-blur-sm border border-white/10 transition-all duration-300`}
        style={{ background: "transparent" }}
      >
        <motion.div className="p-6">
          <div className="flex items-center mb-4">
            <motion.div
              className="w-10 h-10 rounded-full flex items-center justify-center mr-3 text-white"
              style={{ backgroundColor: "var(--primary-color)" }}
            >
              <Icon size={20} />
            </motion.div>
            <h3 className="text-xl font-bold text-white">{title}</h3>
          </div>
          {renderContent()}
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className={`rounded-xl overflow-hidden backdrop-blur-sm border border-white/10 transition-all duration-300`}
      style={{ background: gradient }}
    >
      <motion.div className="p-6">
        <div className="flex items-center mb-4">
          <motion.div
            className="w-10 h-10 rounded-full flex items-center justify-center mr-3 text-white"
            style={{ backgroundColor: "var(--primary-color)" }}
          >
            <Icon size={20} />
          </motion.div>
          <h3 className="text-xl font-bold text-white">{title}</h3>
        </div>
        {renderContent()}
      </motion.div>
    </motion.div>
  );
};

const DynamicDemographicsSection = ({
  demographics,
  scrollProgress,
  palette,
  profileId,
  backgroundImage,
  sectionIndex = 0,
  questionTitles = {},
  responseData,
  questionnaireData,
}) => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const [expandedCardIndex, setExpandedCardIndex] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);

  // Enhanced animation controls
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.95, 1, 0.95]);
  const bgOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.7, 0.9, 0.7]);

  // Determine if this section should have the background on the left or right
  const isLeftAligned = sectionIndex % 2 === 0;

  // Cards per slide for mobile view
  const CARDS_PER_SLIDE = 3;

  // Set up CSS Variables for colors
  useEffect(() => {
    if (palette) {
      document.documentElement.style.setProperty(
        "--primary-color",
        palette.primary
      );
      document.documentElement.style.setProperty(
        "--secondary-color",
        palette.secondary
      );
      document.documentElement.style.setProperty(
        "--accent-color",
        palette.accent
      );

      // Helper function to convert hex to RGB for gradients
      const hexToRgb = (hex) => {
        // Remove # if present
        hex = hex.replace("#", "");

        // Convert 3-digit hex to 6-digits
        if (hex.length === 3) {
          hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        }

        // Extract r, g, b values
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        return `${r}, ${g}, ${b}`;
      };

      // Set RGB values for CSS variables
      try {
        document.documentElement.style.setProperty(
          "--primary-color-rgb",
          hexToRgb(palette.primary)
        );
        document.documentElement.style.setProperty(
          "--secondary-color-rgb",
          hexToRgb(palette.secondary)
        );
        document.documentElement.style.setProperty(
          "--accent-color-rgb",
          hexToRgb(palette.accent)
        );
      } catch (e) {
        console.error("Error setting RGB color values:", e);
      }
    }
  }, [palette]);

  // Define icons for different demographic attributes
  const icons = {
    Age: Calendar,
    Location: MapPin,
    Geography: MapPin,
    Gender: Users,
    Education: GraduationCap,
    Occupation: Briefcase,
    Income: Coins,
    Interests: Heart,
    Behavior: Zap,
    Preferences: Heart,
    Territory: Globe,
    "Age Range": Calendar,
    "Gender Distribution": Users,
    "Interests & Hobbies": Target,
    Lifestyle: Star,
    "Core Values": Heart,
    "Brand Essence": Sparkles,
  };

  // Map demographics data to cards with appropriate icons
  const getDemographicCards = () => {
    return Object.entries(demographics).map(([key, value], index) => {
      // Use displayTitle if available (for duplicate titles with question IDs)
      const displayKey = (typeof value === "object" && value !== null && value.displayTitle) 
        ? value.displayTitle 
        : key;
      
      // Find exact match first
      let Icon = icons[displayKey];

      // If no exact match, try to find partial match
      if (!Icon) {
        const lowerKey = displayKey.toLowerCase();
        for (const [iconKey, iconComponent] of Object.entries(icons)) {
          if (lowerKey.includes(iconKey.toLowerCase())) {
            Icon = iconComponent;
            break;
          }
        }
      }

      // Default to Users icon if no match found
      Icon = Icon || Users;

      return {
        title: displayKey, // Use display title for duplicate titles
        value: value,
        icon: Icon,
        index,
      };
    });
  };

  const demographicCards = getDemographicCards();

  // Group items into slides for mobile navigation
  const itemSlides = [];
  for (let i = 0; i < demographicCards.length; i += CARDS_PER_SLIDE) {
    itemSlides.push(demographicCards.slice(i, i + CARDS_PER_SLIDE));
  }

  const toggleCard = (index) => {
    setExpandedCardIndex(expandedCardIndex === index ? null : index);
  };

  const nextSlide = () => {
    setActiveSlide((prev) => (prev < itemSlides.length - 1 ? prev + 1 : 0));
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev > 0 ? prev - 1 : itemSlides.length - 1));
  };

  return (
    <motion.section
      ref={sectionRef}
      className="min-h-screen relative flex items-center justify-center py-24 overflow-hidden"
      style={{
        opacity,
        backgroundColor: "rgba(5, 8, 15, 0.95)", // Much darker background
      }}
    >
      {/* Background image with transparency mask blending */}
      {backgroundImage && (
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <motion.div
            className={`absolute inset-0 h-full bg-center bg-cover bg-no-repeat`}
            style={{
              backgroundImage: `url(${backgroundImage})`,
              opacity: bgOpacity,
              filter: "contrast(1.2) brightness(1.15)",
              mixBlendMode: "soft-light",
              width: "75%",
              left: isLeftAligned ? "0" : "auto",
              right: isLeftAligned ? "auto" : "0",
              // Apply transparency mask for seamless blending
              maskImage: isLeftAligned
                ? "linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)"
                : "linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)",
              WebkitMaskImage: isLeftAligned
                ? "linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)"
                : "linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)",
            }}
          />
        </div>
      )}

      {/* Content container with proper positioning */}
      <div
        className={`relative z-10 w-full max-w-7xl mx-auto px-4 ${
          isLeftAligned ? "ml-auto" : "mr-auto"
        }`}
      >
        <motion.h2
          className="text-5xl md:text-7xl mb-16 font-bold text-white"
          style={{ textShadow: "0 2px 6px rgba(0, 0, 0, 0.5)" }}
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Target Demographics
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
          className="text-white/60 max-w-3xl mb-12 text-xl"
        >
          Understanding of the key audience segments that the brand serves and
          targets.
        </motion.p>

        {/* AI-Generated Narrative Section */}
        {profileId && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="flex items-center mb-6">
              <motion.div
                className="w-12 h-12 rounded-full flex items-center justify-center mr-4 text-white"
                style={{ backgroundColor: "var(--primary-color)" }}
                whileHover={{ scale: 1.1 }}
              >
                <MessageCircle size={24} />
              </motion.div>
              <h3 className="text-3xl font-bold text-white">
                Demographics Analysis
              </h3>
            </div>

            <OriginalResponsesDisplay
              sectionType="demographics"
              responseData={responseData}
              questionnaireData={questionnaireData}
              textColor="rgba(255, 255, 255, 0.9)"
              accentColor="var(--primary-color)"
            />
          </motion.div>
        )}

        {/* Main Content */}
        <div className="space-y-10">
          {/* Mobile View: Interactive Cards Carousel (now used for all screen sizes) */}
          <div className="relative">
            <div className="flex justify-between items-center mb-4">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={prevSlide}
                className="bg-white/10 rounded-full p-2"
                disabled={itemSlides.length <= 1}
              >
                <ChevronLeft
                  className={`${
                    itemSlides.length <= 1 ? "text-white/30" : "text-white"
                  }`}
                  size={20}
                />
              </motion.button>

              <div className="text-white/60 text-sm">
                {itemSlides.length > 0
                  ? `${activeSlide + 1} / ${itemSlides.length}`
                  : "0 / 0"}
              </div>

              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={nextSlide}
                className="bg-white/10 rounded-full p-2"
                disabled={itemSlides.length <= 1}
              >
                <ChevronRight
                  className={`${
                    itemSlides.length <= 1 ? "text-white/30" : "text-white"
                  }`}
                  size={20}
                />
              </motion.button>
            </div>

            <AnimatePresence mode="wait">
              {itemSlides.length > 0 ? (
                <motion.div
                  key={activeSlide}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-4"
                >
                  {itemSlides[activeSlide].map((card, index) => (
                    <DemographicCard
                      key={index}
                      title={card.title}
                      value={card.value}
                      icon={card.icon}
                      index={index + activeSlide * CARDS_PER_SLIDE}
                      isExpanded={
                        expandedCardIndex ===
                        index + activeSlide * CARDS_PER_SLIDE
                      }
                      onToggle={() =>
                        toggleCard(index + activeSlide * CARDS_PER_SLIDE)
                      }
                      palette={palette}
                      questionTitles={questionTitles}
                    />
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-8 text-center bg-white/5 rounded-lg border border-white/10"
                >
                  <p className="text-white/70">
                    No content is available to display.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Pagination dots - only show if there are multiple slides */}
            {itemSlides.length > 1 && (
              <div className="flex justify-center mt-6 space-x-2">
                {itemSlides.map((_, index) => (
                  <motion.button
                    key={index}
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor:
                        activeSlide === index
                          ? "var(--primary-color)"
                          : "rgba(255, 255, 255, 0.3)",
                    }}
                    onClick={() => setActiveSlide(index)}
                    whileHover={{ scale: 1.3 }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default DynamicDemographicsSection;
